import React, { useEffect, useMemo, useState } from "react";
import { Search, X, ShoppingBag, Mail, MapPin, Phone, Repeat, Award, BadgeAlert, ExternalLink } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch { return iso?.slice(0, 10) || "—"; }
};

const fmtMoney = (n) => `£${Number(n || 0).toFixed(2)}`;

const tierLabel = (paid) => {
  if (paid >= 5) return { label: "VIP · 5+", cls: "bg-[#A6FF00]/15 text-[#A6FF00] border-[#A6FF00]/35" };
  if (paid >= 3) return { label: `${paid}× returning`, cls: "bg-[#5EEAD4]/15 text-[#5EEAD4] border-[#5EEAD4]/35" };
  if (paid === 2) return { label: "2× returning", cls: "bg-[#00C2D6]/15 text-[#00C2D6] border-[#00C2D6]/35" };
  if (paid === 1) return { label: "1st paid", cls: "bg-[#1E2E44] text-[#9CA8B8] border-[#1E2E44]" };
  return { label: "no paid orders", cls: "bg-[#1E2E44] text-[#9CA8B8] border-[#1E2E44]" };
};

export default function AdminCustomers() {
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all | returning | onetime | unsub
  const [sortBy, setSortBy] = useState("last_order_at"); // last_order_at | total_spent | paid_order_count
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null); // {email, ...} when modal open
  const [detailLoading, setDetailLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/customers");
      setItems(r.data.items || []);
      setCount(r.data.count || 0);
    } catch (e) {
      toast.error("Failed to load customers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let rows = items;
    if (filter === "returning") rows = rows.filter((r) => r.is_returning);
    else if (filter === "onetime") rows = rows.filter((r) => r.paid_order_count === 1);
    else if (filter === "unsub") rows = rows.filter((r) => r.unsubscribed);
    if (search.trim()) {
      const s = search.toLowerCase().trim();
      rows = rows.filter((r) =>
        (r.email || "").toLowerCase().includes(s) ||
        (r.name || "").toLowerCase().includes(s) ||
        (r.city || "").toLowerCase().includes(s)
      );
    }
    const sorted = [...rows].sort((a, b) => {
      if (sortBy === "total_spent") return (b.total_spent || 0) - (a.total_spent || 0);
      if (sortBy === "paid_order_count") return (b.paid_order_count || 0) - (a.paid_order_count || 0);
      // default: last_order_at desc
      return (b.last_order_at || "").localeCompare(a.last_order_at || "");
    });
    return sorted;
  }, [items, filter, search, sortBy]);

  const totals = useMemo(() => {
    const returning = items.filter((r) => r.is_returning).length;
    const onetime = items.filter((r) => r.paid_order_count === 1).length;
    const lifetimeValue = items.reduce((acc, r) => acc + (r.total_spent || 0), 0);
    return { returning, onetime, lifetimeValue };
  }, [items]);

  const openDetail = async (email) => {
    setDetail({ email, loading: true });
    setDetailLoading(true);
    try {
      const r = await api.get(`/admin/customers/${encodeURIComponent(email)}`);
      setDetail(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load customer");
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <div data-testid="admin-customers-page">
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="pg-micro mb-2">// Customer Directory</div>
          <h1 className="text-[#F7FAFC] text-3xl pg-display" style={{ fontFamily: "'Exo 2'" }}>
            Customers
          </h1>
          <p className="text-[#9CA8B8] text-sm mt-1">
            Every distinct email that has ordered, with re-order counts and lifetime spend.
          </p>
        </div>
        <button onClick={load} className="pg-btn-ghost text-xs" data-testid="reload-customers">
          Refresh
        </button>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatCard label="Total customers" value={count} icon={<Mail size={14} />} testid="stat-total" />
        <StatCard label="Returning (2+ paid)" value={totals.returning} icon={<Repeat size={14} />} accent="lime" testid="stat-returning" />
        <StatCard label="1st-paid only" value={totals.onetime} icon={<Award size={14} />} testid="stat-onetime" />
        <StatCard label="Lifetime revenue" value={fmtMoney(totals.lifetimeValue)} icon={<ShoppingBag size={14} />} accent="cyan" testid="stat-lifetime" />
      </div>

      {/* Filters */}
      <div className="pg-card p-4 mb-4 flex flex-col md:flex-row gap-3 items-stretch md:items-center">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA8B8]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by email, name or city…"
            className="pg-input pl-9 w-full text-sm"
            data-testid="customer-search"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[#9CA8B8] hover:text-[#F7FAFC]"
              data-testid="customer-search-clear"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {[
            { id: "all", label: "All" },
            { id: "returning", label: "Returning" },
            { id: "onetime", label: "1st-paid" },
            { id: "unsub", label: "Unsubscribed" },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`px-3 py-1.5 text-[10px] pg-mono uppercase tracking-[0.2em] border rounded-sm ${
                filter === f.id
                  ? "border-[#00C2D6] text-[#00C2D6] bg-[#00C2D6]/10"
                  : "border-[#00C2D6]/20 text-[#9CA8B8] hover:text-[#F7FAFC]"
              }`}
              data-testid={`customer-filter-${f.id}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 text-[10px] pg-mono uppercase tracking-[0.2em] text-[#9CA8B8]">
          Sort:
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="pg-input text-xs py-1.5"
            data-testid="customer-sort"
          >
            <option value="last_order_at">Last order</option>
            <option value="total_spent">Lifetime spend</option>
            <option value="paid_order_count">Paid orders</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="pg-card divide-y divide-[#00C2D6]/10">
        <div className="hidden md:grid grid-cols-12 gap-3 px-5 py-3 text-[10px] pg-mono uppercase tracking-[0.18em] text-[#5EEAD4]">
          <div className="col-span-4">Customer</div>
          <div className="col-span-2">Orders (paid / total)</div>
          <div className="col-span-2">Lifetime spend</div>
          <div className="col-span-2">Last order</div>
          <div className="col-span-2 text-right">Tier</div>
        </div>
        {loading ? (
          <div className="text-[#9CA8B8] text-center py-12">Loading customers…</div>
        ) : filtered.length === 0 ? (
          <div className="text-[#9CA8B8] text-center py-12">No customers match this filter</div>
        ) : (
          filtered.map((c) => {
            const tier = tierLabel(c.paid_order_count);
            return (
              <button
                key={c.email}
                onClick={() => openDetail(c.email)}
                className="w-full text-left px-5 py-4 hover:bg-[#111A2B] transition-colors grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-3 items-center"
                data-testid={`customer-row-${c.email}`}
              >
                <div className="md:col-span-4 min-w-0">
                  <div className="text-[#F7FAFC] text-sm truncate flex items-center gap-2">
                    {c.name || "(no name)"}
                    {c.unsubscribed && (
                      <span className="text-[9px] pg-mono uppercase tracking-[0.18em] text-[#FF8A5B] border border-[#FF8A5B]/35 rounded-sm px-1.5 py-0.5">
                        unsub
                      </span>
                    )}
                  </div>
                  <div className="text-[#9CA8B8] text-xs pg-mono truncate">{c.email}</div>
                  {c.city && (
                    <div className="text-[#9CA8B8] text-[11px] flex items-center gap-1 mt-0.5">
                      <MapPin size={10} /> {c.city}
                    </div>
                  )}
                </div>
                <div className="md:col-span-2 text-sm text-[#F7FAFC] pg-mono">
                  <span className="text-[#A6FF00]">{c.paid_order_count}</span>
                  <span className="text-[#9CA8B8]"> / {c.order_count}</span>
                </div>
                <div className="md:col-span-2 text-sm text-[#A6FF00] pg-mono">
                  {fmtMoney(c.total_spent)}
                </div>
                <div className="md:col-span-2 text-sm text-[#9CA8B8]">
                  {fmtDate(c.last_order_at)}
                  {c.last_order_number && (
                    <div className="text-[10px] pg-mono text-[#00C2D6] truncate">{c.last_order_number}</div>
                  )}
                </div>
                <div className="md:col-span-2 md:text-right">
                  <span className={`inline-block text-[10px] pg-mono uppercase tracking-[0.16em] border rounded-sm px-2 py-1 ${tier.cls}`}>
                    {tier.label}
                  </span>
                </div>
              </button>
            );
          })
        )}
      </div>

      {detail && (
        <CustomerDetailModal
          detail={detail}
          loading={detailLoading}
          onClose={() => setDetail(null)}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, icon, accent, testid }) {
  const accentClass =
    accent === "lime" ? "text-[#A6FF00]" :
    accent === "cyan" ? "text-[#00C2D6]" :
    "text-[#F7FAFC]";
  return (
    <div className="pg-card p-4" data-testid={testid}>
      <div className="flex items-center gap-2 pg-micro mb-2 text-[#5EEAD4]">
        {icon} {label}
      </div>
      <div className={`text-2xl pg-mono ${accentClass}`}>
        {typeof value === "number" ? value.toLocaleString() : value}
      </div>
    </div>
  );
}

function CustomerDetailModal({ detail, loading, onClose }) {
  if (!detail) return null;
  const orders = detail.orders || [];
  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={onClose}
      data-testid="customer-detail-modal"
    >
      <div
        className="bg-[#0F172A] border border-[#00C2D6]/40 rounded-sm w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-5 py-4 border-b border-[#1E2E44] gap-4">
          <div className="min-w-0 flex-1">
            <div className="pg-micro text-[#5EEAD4] mb-1">// Customer Detail</div>
            <h2 className="text-[#F7FAFC] text-lg pg-heading truncate" style={{ fontFamily: "'Rajdhani'" }}>
              {detail.name || "(no name)"}
            </h2>
            <div className="pg-mono text-[#9CA8B8] text-xs truncate">{detail.email}</div>
            {detail.unsubscribed && (
              <div className="mt-2 text-[10px] pg-mono uppercase tracking-[0.18em] text-[#FF8A5B] border border-[#FF8A5B]/35 rounded-sm px-2 py-1 inline-flex items-center gap-1">
                <BadgeAlert size={11} /> Unsubscribed{detail.unsubscribe_reason ? ` · ${detail.unsubscribe_reason}` : ""}
              </div>
            )}
          </div>
          <button onClick={onClose} className="text-[#9CA8B8] hover:text-[#F7FAFC]" data-testid="close-customer-detail">
            <X size={18} />
          </button>
        </div>

        {loading ? (
          <div className="text-[#9CA8B8] text-center py-16">Loading…</div>
        ) : (
          <div className="overflow-y-auto">
            {/* Top summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 px-5 py-4 border-b border-[#1E2E44]">
              <SummaryCell label="Paid orders" value={detail.paid_order_count} accent="lime" />
              <SummaryCell label="Total orders" value={detail.order_count} />
              <SummaryCell label="Lifetime spend" value={fmtMoney(detail.total_spent)} accent="lime" />
              <SummaryCell label="First / last" value={`${fmtDate(detail.first_order_at)} → ${fmtDate(detail.last_order_at)}`} small />
            </div>

            {/* Contact details */}
            <div className="px-5 py-4 border-b border-[#1E2E44] text-sm text-[#F7FAFC] space-y-1">
              {detail.phone && (
                <div className="flex items-center gap-2"><Phone size={12} className="text-[#9CA8B8]" /> {detail.phone}</div>
              )}
              {(detail.address_line1 || detail.city || detail.postcode) && (
                <div className="flex items-start gap-2">
                  <MapPin size={12} className="text-[#9CA8B8] mt-1" />
                  <span className="text-[13px]">
                    {[detail.address_line1, detail.address_line2, detail.city, detail.postcode, detail.country]
                      .filter(Boolean).join(", ")}
                  </span>
                </div>
              )}
              <div className="text-[11px] pg-mono uppercase tracking-[0.18em] text-[#9CA8B8] pt-1">
                Marketing consent: {detail.marketing_consent ? (
                  <span className="text-[#A6FF00]">YES</span>
                ) : (
                  <span className="text-[#FF8A5B]">NO</span>
                )}
              </div>
            </div>

            {/* Orders list */}
            <div className="px-5 py-4">
              <div className="pg-micro mb-3">// Order History ({orders.length})</div>
              <div className="space-y-2">
                {orders.map((o, idx) => (
                  <div
                    key={o.id || o.order_number}
                    className="grid grid-cols-2 md:grid-cols-6 gap-2 items-center px-3 py-3 border border-[#1E2E44] rounded-sm bg-[#0B1220] text-sm"
                    data-testid={`customer-order-${o.order_number}`}
                  >
                    <div>
                      <div className="text-[10px] pg-mono uppercase tracking-[0.18em] text-[#5EEAD4] mb-0.5">
                        #{orders.length - idx}
                      </div>
                      <div className="pg-mono text-[#F7FAFC] text-xs truncate">{o.order_number}</div>
                    </div>
                    <div className="text-[#9CA8B8] text-xs">{fmtDate(o.created_at)}</div>
                    <div className="text-[#F7FAFC] text-xs truncate col-span-2">
                      {(o.items || []).map((i) => `${i.name} ${i.strength || ""} ×${i.quantity}`).join(", ") || "—"}
                    </div>
                    <div className="text-[#A6FF00] pg-mono">{fmtMoney(o.total)}</div>
                    <div className="flex items-center justify-end gap-2 flex-wrap">
                      <StatusPill
                        label={o.payment_status || "—"}
                        ok={o.payment_status === "paid"}
                      />
                      <StatusPill label={o.status || "—"} neutral />
                    </div>
                  </div>
                ))}
                {orders.length === 0 && (
                  <div className="text-[#9CA8B8] text-center py-8 text-sm">No orders found.</div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="px-5 py-4 border-t border-[#1E2E44] flex justify-between items-center gap-2 flex-wrap">
          <div className="text-[10px] pg-mono uppercase tracking-[0.18em] text-[#9CA8B8]">
            Click an order in Admin → Orders to dispatch / edit
          </div>
          <a
            href="/admin/orders"
            className="pg-btn-ghost text-xs flex items-center gap-1"
            data-testid="goto-admin-orders"
          >
            Open Orders <ExternalLink size={12} />
          </a>
        </div>
      </div>
    </div>
  );
}

function SummaryCell({ label, value, accent, small }) {
  const accentClass = accent === "lime" ? "text-[#A6FF00]" : "text-[#F7FAFC]";
  return (
    <div className="px-3 py-2 border border-[#1E2E44] rounded-sm bg-[#0B1220]">
      <div className="pg-micro text-[#5EEAD4] mb-1">{label}</div>
      <div className={`${small ? "text-[12px]" : "text-lg pg-mono"} ${accentClass}`}>
        {typeof value === "number" ? value.toLocaleString() : value}
      </div>
    </div>
  );
}

function StatusPill({ label, ok, neutral }) {
  const cls = ok
    ? "text-[#A6FF00] border-[#A6FF00]/35 bg-[#A6FF00]/10"
    : neutral
      ? "text-[#9CA8B8] border-[#1E2E44] bg-[#0B1220]"
      : "text-[#FF8A5B] border-[#FF8A5B]/35 bg-[#FF8A5B]/10";
  return (
    <span className={`text-[10px] pg-mono uppercase tracking-[0.16em] border rounded-sm px-2 py-0.5 ${cls}`}>
      {label}
    </span>
  );
}
